import {
  type GuidyCommandMessage,
  type GuidyElement,
  type GuidyElementsMessage,
  type GuidyPointAckMessage,
  type GuidyPointMessage,
  type GuidyRoute,
  type GuidyRoutesMessage
} from '../types';

/** Post an app → host message (elements, routes, or a point ack). */
type GuidyBridgeSender = (
  message: GuidyElementsMessage | GuidyRoutesMessage | GuidyPointAckMessage
) => void;

/** Ids with this prefix belong to the host assistant and are never scanned. */
const GUIDY_INTERNAL_PREFIX = 'guidy-';

const HIGHLIGHT_CLASS = 'guidy-bridge-highlight';
const HIGHLIGHT_STYLE_ID = 'guidy-bridge-style';
const ANNOTATION_ID = 'guidy-bridge-annotation';
const HIGHLIGHT_DURATION_MS = 5200;
/** Debounce window for re-posting the inventory after DOM mutations. */
const SCAN_DEBOUNCE_MS = 250;
/** Delay before the synthetic click so the highlight is visible first. */
const CLICK_DELAY_MS = 450;

function elementLabel(node: HTMLElement): string {
  const aria = node.getAttribute('aria-label')?.trim();

  if (aria) return aria;

  const text = node.textContent?.replace(/\s+/g, ' ').trim();

  if (text) return text;

  return node.getAttribute('title')?.trim() ?? '';
}

function isVisible(node: HTMLElement): boolean {
  const rect = node.getBoundingClientRect();

  if (rect.width === 0 && rect.height === 0) return false;

  const style = window.getComputedStyle(node);

  return style.visibility !== 'hidden' && style.display !== 'none';
}

/**
 * Runs inside an embedded Docyrus app and lets the host assistant (Guidy)
 * inspect and drive the app's visible UI over the existing postMessage
 * channel.
 *
 * It only ever exposes labels/ids of already-visible interactive elements and
 * only executes a fixed command vocabulary (scan / point / click) — the same
 * exposure profile the host assistant already has on its own chrome. No
 * arbitrary script is accepted from the host.
 *
 * Protocol (paired with the host's ExternalAppManager):
 * - App → Host: `guidy:elements`   (pushed on start, route change, mutation)
 * - App → Host: `guidy:routes`     (pushed on start and when routes change)
 * - Host → App: `guidy:scan`       → app re-posts `guidy:elements` + `guidy:routes`
 * - Host → App: `guidy:point`      → app scrolls/highlights/(clicks), then
 * - App → Host: `guidy:point-ack`
 */
export class GuidyBridge {
  private send: GuidyBridgeSender;
  private routes: GuidyRoute[];
  private started = false;
  private observer: MutationObserver | null = null;
  private scanTimer: ReturnType<typeof setTimeout> | null = null;
  private routeListener: (() => void) | null = null;
  private highlightTimer: ReturnType<typeof setTimeout> | null = null;
  private clickTimer: ReturnType<typeof setTimeout> | null = null;
  private annotationTimer: ReturnType<typeof setTimeout> | null = null;
  constructor(send: GuidyBridgeSender, routes: GuidyRoute[] = []) {
    this.send = send;
    this.routes = routes;
  }
  /** Replace the declared navigable routes and re-post them to the host. */
  setRoutes(routes: GuidyRoute[]): void {
    this.routes = routes;
    this.postRoutes();
  }
  /**
   * Begin reporting the element inventory and watching for changes.
   * Idempotent. No-op outside a browser document.
   */
  start(): void {
    if (this.started || typeof document === 'undefined') return;
    this.started = true;

    this.ensureStyle();
    this.postElements();
    this.postRoutes();

    this.observer = new MutationObserver(() => this.scheduleScan());
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'id',
        'hidden',
        'aria-label',
        'title',
        'style'
      ]
    });

    this.routeListener = () => this.scheduleScan();
    window.addEventListener('popstate', this.routeListener);
    window.addEventListener('hashchange', this.routeListener);
  }
  /** Stop watching and tear down any injected overlay. Idempotent. */
  stop(): void {
    if (!this.started) return;
    this.started = false;

    this.observer?.disconnect();
    this.observer = null;

    if (this.routeListener) {
      window.removeEventListener('popstate', this.routeListener);
      window.removeEventListener('hashchange', this.routeListener);
      this.routeListener = null;
    }

    this.clearTimer('scanTimer');
    this.clearTimer('highlightTimer');
    this.clearTimer('clickTimer');
    this.clearTimer('annotationTimer');
    this.clearHighlight();
    this.clearAnnotation();
  }
  /** Dispatch a host `guidy:*` command. Wired to `IframeAuth.onGuidyCommand`. */
  handleCommand(message: GuidyCommandMessage): void {
    if (message.type === 'guidy:scan') {
      this.postElements();
      this.postRoutes();

      return;
    }

    if (message.type === 'guidy:point') {
      this.handlePoint(message);
    }
  }
  /** Collect every visible anchor/button that exposes an id and a label. */
  private collectElements(): GuidyElement[] {
    if (typeof document === 'undefined') return [];

    const nodes = document.querySelectorAll<HTMLElement>('a[id], button[id]');
    const seen = new Set<string>();
    const result: GuidyElement[] = [];

    nodes.forEach((node) => {
      const { id } = node;

      if (!id || id.startsWith(GUIDY_INTERNAL_PREFIX) || seen.has(id)) return;
      if (!isVisible(node)) return;

      const label = elementLabel(node);

      if (!label) return;

      seen.add(id);
      result.push({
        id,
        label,
        tag: node.tagName.toLowerCase() === 'a' ? 'a' : 'button'
      });
    });

    return result;
  }
  private postElements(): void {
    if (!this.started) return;

    const path = typeof window === 'undefined' ? '' : window.location.pathname;

    this.send({
      type: 'guidy:elements',
      path,
      elements: this.collectElements()
    });
  }
  private postRoutes(): void {
    if (!this.started) return;

    this.send({ type: 'guidy:routes', routes: this.routes });
  }
  private scheduleScan(): void {
    if (this.scanTimer) return;

    this.scanTimer = setTimeout(() => {
      this.scanTimer = null;
      this.postElements();
    }, SCAN_DEBOUNCE_MS);
  }
  private handlePoint(message: GuidyPointMessage): void {
    const element = typeof document === 'undefined' ? null : document.getElementById(message.id);

    if (!element) {
      this.send({ type: 'guidy:point-ack', id: message.id, ok: false });

      return;
    }

    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
    this.highlight(element);

    if (message.message) {
      this.annotate(element, message.message);
    }

    if (message.click) {
      this.clearTimer('clickTimer');
      this.clickTimer = setTimeout(() => {
        this.clickTimer = null;
        element.click();
      }, CLICK_DELAY_MS);
    }

    this.send({ type: 'guidy:point-ack', id: message.id, ok: true });
  }
  private ensureStyle(): void {
    if (typeof document === 'undefined') return;
    if (document.getElementById(HIGHLIGHT_STYLE_ID)) return;

    const style = document.createElement('style');

    style.id = HIGHLIGHT_STYLE_ID;
    style.textContent = `
      .${HIGHLIGHT_CLASS} {
        outline: 2px solid #3b82f6 !important;
        outline-offset: 2px;
        border-radius: 8px;
        animation: guidy-bridge-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
      }
      @keyframes guidy-bridge-ping {
        0% {
          box-shadow: 0 0 0 0 color-mix(in srgb, #3b82f6 50%, transparent);
        }
        75%, 100% {
          box-shadow: 0 0 0 10px color-mix(in srgb, #3b82f6 0%, transparent);
        }
      }
      #${ANNOTATION_ID} {
        position: fixed;
        z-index: 2147483647;
        max-width: 240px;
        padding: 8px 12px;
        border-radius: 10px;
        background: #111827;
        color: #f9fafb;
        font: 13px/1.4 system-ui, -apple-system, sans-serif;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }
  private highlight(element: HTMLElement): void {
    this.clearTimer('highlightTimer');
    this.clearHighlight();
    element.classList.add(HIGHLIGHT_CLASS);
    this.highlightTimer = setTimeout(() => {
      this.highlightTimer = null;
      this.clearHighlight();
    }, HIGHLIGHT_DURATION_MS);
  }
  private clearHighlight(): void {
    if (typeof document === 'undefined') return;

    document
      .querySelectorAll(`.${HIGHLIGHT_CLASS}`)
      .forEach(node => node.classList.remove(HIGHLIGHT_CLASS));
  }
  private annotate(element: HTMLElement, message: string): void {
    if (typeof document === 'undefined') return;

    this.clearTimer('annotationTimer');
    this.clearAnnotation();

    const bubble = document.createElement('div');

    bubble.id = ANNOTATION_ID;
    bubble.textContent = message;
    document.body.appendChild(bubble);

    const rect = element.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    const gap = 10;
    let top = rect.bottom + gap;

    if (top + bubbleRect.height > window.innerHeight) {
      top = Math.max(gap, rect.top - bubbleRect.height - gap);
    }

    const left = Math.min(
      Math.max(gap, rect.left),
      window.innerWidth - bubbleRect.width - gap
    );

    bubble.style.top = `${Math.round(top)}px`;
    bubble.style.left = `${Math.round(left)}px`;

    this.annotationTimer = setTimeout(() => {
      this.annotationTimer = null;
      this.clearAnnotation();
    }, HIGHLIGHT_DURATION_MS);
  }
  private clearAnnotation(): void {
    if (typeof document === 'undefined') return;

    document.getElementById(ANNOTATION_ID)?.remove();
  }
  private clearTimer(
    key: 'scanTimer' | 'highlightTimer' | 'clickTimer' | 'annotationTimer'
  ): void {
    const timer = this[key];

    if (timer) {
      clearTimeout(timer);
      this[key] = null;
    }
  }
}
